import { useState, useEffect } from 'react';
import api from '../../services/api';
import { useI18n } from '../../i18n';

const now = new Date();

export default function AdminTeam() {
  const { t, locale } = useI18n();
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [pdfUser, setPdfUser] = useState(null);
  const [reportYear, setReportYear] = useState(now.getFullYear());
  const [reportMonth, setReportMonth] = useState(now.getMonth() + 1);
  const [pdfLoading, setPdfLoading] = useState(false);

  const MONTH_NAMES = Array.from({ length: 12 }, (_, i) => new Date(2000, i, 1).toLocaleString(locale, { month: 'long' }));

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/time/team');
      setTeam(res.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const interval = setInterval(load, 60000);
    return () => clearInterval(interval);
  }, []);

  const handleDownloadPdf = async (user) => {
    setPdfUser(user.user_id);
    setPdfLoading(true);
    try {
      const res = await api.get(
        `/reports/preview/${user.user_id}/${reportYear}/${reportMonth}`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${t('admin.team.reportFile')}_${user.personnel_number}_${reportYear}_${String(reportMonth).padStart(2, '0')}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } finally {
      setPdfUser(null);
      setPdfLoading(false);
    }
  };

  const filtered = team.filter((m) => {
    if (filter === 'in') return m.checked_in;
    if (filter === 'out') return !m.checked_in;
    return true;
  });

  const checkedInCount = team.filter((m) => m.checked_in).length;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <h4 className="mb-0"><i className="bi bi-people-fill me-2" />{t('admin.team.title')}</h4>
        <div className="d-flex gap-2 align-items-center flex-wrap">
          <select className="form-select form-select-sm" style={{width:120}} value={reportMonth} onChange={(e) => setReportMonth(+e.target.value)}>
            {MONTH_NAMES.map((n, i) => <option key={i+1} value={i+1}>{n}</option>)}
          </select>
          <select className="form-select form-select-sm" style={{width:90}} value={reportYear} onChange={(e) => setReportYear(+e.target.value)}>
            {[now.getFullYear()-1, now.getFullYear()].map(y => <option key={y}>{y}</option>)}
          </select>
          <span className="text-muted small">{t('admin.team.refreshHint')}</span>
          <button className="btn btn-sm btn-outline-secondary" onClick={load}><i className="bi bi-arrow-clockwise" /></button>
        </div>
      </div>

      <div className="row g-3 mb-4">
        <div className="col-4">
          <div className="card text-center">
            <div className="card-body py-2">
              <div className="fs-4 fw-bold text-primary">{team.length}</div>
              <div className="text-muted small">{t('admin.team.total')}</div>
            </div>
          </div>
        </div>
        <div className="col-4">
          <div className="card text-center">
            <div className="card-body py-2">
              <div className="fs-4 fw-bold text-success">{checkedInCount}</div>
              <div className="text-muted small">{t('admin.team.checkedIn')}</div>
            </div>
          </div>
        </div>
        <div className="col-4">
          <div className="card text-center">
            <div className="card-body py-2">
              <div className="fs-4 fw-bold text-secondary">{team.length - checkedInCount}</div>
              <div className="text-muted small">{t('admin.team.notPresent')}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="mb-3">
        <div className="btn-group btn-group-sm">
          {[['all', t('admin.team.filterAll')], ['in', t('admin.team.filterIn')], ['out', t('admin.team.filterOut')]].map(([val, label]) => (
            <button key={val} className={`btn ${filter === val ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => setFilter(val)}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-center"><div className="spinner-border" /></div>
      ) : (
        <div className="row g-3">
          {filtered.map((m) => (
            <div className="col-md-4 col-sm-6" key={m.user_id}>
              <div className={`card ${m.checked_in ? 'border-success' : 'border-light'}`}>
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <div className="fw-bold">{m.first_name} {m.last_name}</div>
                      <code className="text-muted small">{m.personnel_number}</code>
                    </div>
                    <div className="d-flex flex-column align-items-end gap-1">
                      {m.checked_in ? (
                        <span className={`badge ${m.work_location === 'HOME_OFFICE' ? 'bg-info' : 'bg-success'}`}>
                          <i className={`bi ${m.work_location === 'HOME_OFFICE' ? 'bi-house' : 'bi-building'} me-1`} />
                          {m.work_location === 'HOME_OFFICE' ? t('admin.team.homeOffice') : t('admin.team.office')}
                        </span>
                      ) : (
                        <span className="badge bg-secondary">{t('admin.team.absent')}</span>
                      )}
                      <button
                        className="btn btn-outline-danger btn-sm py-0"
                        style={{fontSize:'0.7rem'}}
                        onClick={() => handleDownloadPdf(m)}
                        disabled={pdfLoading && pdfUser === m.user_id}
                        title={t('admin.team.pdfTitle', { month: MONTH_NAMES[reportMonth-1], year: reportYear })}
                      >
                        <i className="bi bi-file-earmark-pdf me-1" />
                        {pdfLoading && pdfUser === m.user_id ? '…' : 'PDF'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
          {filtered.length === 0 && <div className="text-muted">{t('admin.team.empty')}</div>}
        </div>
      )}
    </div>
  );
}
